import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/contexts/SettingsContext";
import { Phone, MessageCircle, Mail, MapPin, Clock, Instagram, Facebook } from "lucide-react";

const LEGAL = [
  { to: "/legal/mentions", key: "mentions" },
  { to: "/legal/cgv", key: "cgv" },
  { to: "/legal/cgu", key: "cgu" },
  { to: "/legal/privacy", key: "privacy" },
  { to: "/legal/cookies", key: "cookies" },
  { to: "/legal/accessibility", key: "accessibility" },
  { to: "/legal/sitemap", key: "sitemap" },
];

export default function Footer() {
  const { t, i18n } = useTranslation();
  const { settings: s } = useSettings();
  const lang = i18n.language;
  const hours = s?.hours?.[lang] || s?.hours?.fr || "";

  return (
    <footer data-testid="site-footer" className="bg-[#050505] border-t border-[#D4AF37]/15 mt-24">
      <div className="gold-line opacity-50" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <img src="/logo.png" alt="Le Dix Sept" className="h-20 w-auto mb-4" />
          <div className="overline mb-4">{t("footer.tagline")}</div>
          <p className="text-sm text-[#F8F5EF]/70 max-w-sm leading-relaxed">
            {t("hero.subtitle")}
          </p>
          {(s?.instagram_url || s?.facebook_url) && (
            <div className="flex gap-4 mt-6">
              {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:text-[#E5C869]" data-testid="footer-instagram"><Instagram size={20} /></a>}
              {s.facebook_url && <a href={s.facebook_url} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:text-[#E5C869]" data-testid="footer-facebook"><Facebook size={20} /></a>}
            </div>
          )}
        </div>

        <div className="md:col-span-4 space-y-4 text-sm">
          <div className="overline mb-3">{t("contact_page.title")}</div>
          <a href={`tel:${(s?.phone || "").replace(/\s/g, "")}`} className="flex items-start gap-3 text-[#F8F5EF]/80 hover:text-[#D4AF37]" data-testid="footer-phone">
            <Phone size={16} className="text-[#D4AF37] mt-1" />
            <span>{s?.phone}</span>
          </a>
          <a href={`https://wa.me/${s?.whatsapp_raw}`} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-[#F8F5EF]/80 hover:text-[#D4AF37]" data-testid="footer-whatsapp">
            <MessageCircle size={16} className="text-[#D4AF37] mt-1" />
            <span>{s?.whatsapp}</span>
          </a>
          <a href={`mailto:${s?.email}`} className="flex items-start gap-3 text-[#F8F5EF]/80 hover:text-[#D4AF37]" data-testid="footer-email">
            <Mail size={16} className="text-[#D4AF37] mt-1" />
            <span>{s?.email}</span>
          </a>
          <a href={s?.maps_url} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-[#F8F5EF]/80 hover:text-[#D4AF37]" data-testid="footer-address">
            <MapPin size={16} className="text-[#D4AF37] mt-1" />
            <span>{s?.address}</span>
          </a>
          <div className="flex items-start gap-3 text-[#F8F5EF]/80">
            <Clock size={16} className="text-[#D4AF37] mt-1" />
            <span className="whitespace-pre-line">{hours}</span>
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="overline mb-3">{t("footer.legal")}</div>
          <ul className="space-y-2 text-sm">
            {LEGAL.map(l => (
              <li key={l.key}>
                <Link to={l.to} className="text-[#F8F5EF]/70 hover:text-[#D4AF37]" data-testid={`footer-legal-${l.key}`}>
                  {t(`footer.links.${l.key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-[#D4AF37]/10 px-6 lg:px-10 py-6 max-w-7xl mx-auto flex flex-col sm:flex-row gap-3 items-center justify-between text-xs text-[#F8F5EF]/50">
        <div>© {new Date().getFullYear()} Le 17 — {t("footer.rights")}</div>
        <div>{t("footer.credit")}</div>
      </div>
    </footer>
  );
}
